import { useMemo, useRef, useState, type ChangeEvent } from 'react'
import { uploadEnrollmentDocumentRequest } from '@/api/enrollment-api'
import type { UploadConfig, UploadFieldKey } from '../uploadDocumentConfig'
import {
  getMissingSessionUploadError,
  getUploadDocumentsErrorMessage,
  MAX_UPLOAD_FILE_SIZE
} from './helpers'
import type { FileState } from './types'

type UseUploadDocumentUploadArgs = {
  enrollmentSessionToken: string | null
  onUploadSuccess: () => void
  setValidationError: (value: string | null) => void
  updateUploadedDocumentFiles: (files: Partial<FileState>) => void
}

export function useUploadDocumentUpload({
  enrollmentSessionToken,
  onUploadSuccess,
  setValidationError,
  updateUploadedDocumentFiles
}: UseUploadDocumentUploadArgs) {
  const [uploadingKeys, setUploadingKeys] = useState<UploadFieldKey[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve())

  const isUploading = uploadingKeys.length > 0

  const uploadingKeySet = useMemo(() => new Set(uploadingKeys), [uploadingKeys])

  const handleFileChange = async (config: UploadConfig, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    const input = event.target

    if (!file) {
      return
    }

    setUploadError(null)
    setValidationError(null)

    if (!enrollmentSessionToken) {
      setUploadError(getMissingSessionUploadError())
      input.value = ''
      return
    }

    if (file.size > MAX_UPLOAD_FILE_SIZE) {
      setUploadError(`${config.title} file must be 10MB or smaller.`)
      input.value = ''
      return
    }

    setUploadingKeys(current => (current.includes(config.key) ? current : [...current, config.key]))

    const uploadTask = uploadQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          const uploadedDocument = await uploadEnrollmentDocumentRequest({
            sessionToken: enrollmentSessionToken,
            documentType: config.documentType,
            file,
            notes: config.notes
          })

          updateUploadedDocumentFiles({
            [config.key]: uploadedDocument.fileName || file.name
          })
          onUploadSuccess()
        } catch (error) {
          setUploadError(getUploadDocumentsErrorMessage(config.title, error))
        } finally {
          setUploadingKeys(current => current.filter(key => key !== config.key))
          input.value = ''
        }
      })

    uploadQueueRef.current = uploadTask

    await uploadTask
  }

  return {
    handleFileChange,
    isUploading,
    uploadError,
    uploadingKeySet
  }
}
